import { queryOptions } from "@tanstack/react-query";
import { getCoComments } from "@/features/co-review/api/coReviewApi";
import type { CoReviewComment } from "../../../shared/interfaces/CoReviewComment";

export type CoCommentsCycle = "all" | "current";

export const coReviewKeys = {
  all: ["coReview"] as const,
  comments: (reportId: string) =>
    [...coReviewKeys.all, "comments", reportId] as const,
  commentList: (reportId: string, cycle: CoCommentsCycle) =>
    [...coReviewKeys.comments(reportId), cycle] as const,
};

/**
 * Decoded, sorted CO comments for a report. The cache stores the exact return
 * of getCoComments — decoding must happen inside the queryFn because it needs
 * the synchronous getUserName (queryClient users cache) and decodeMany's
 * whole-response cycle mapping + reply cycle inheritance.
 *
 * CoReviewPage observes "all"; the edit page observes "current". Mutations
 * must always write/invalidate by the coReviewKeys.comments(reportId) PREFIX
 * so both lists stay coherent.
 *
 * staleTime/gcTime 0 = parity with today's fetch-fresh-per-mount behavior;
 * review state is mutable legal state, never serve it stale across mounts.
 */
export function coCommentsQuery(reportId: string, cycle: CoCommentsCycle) {
  return queryOptions<CoReviewComment[]>({
    queryKey: coReviewKeys.commentList(reportId, cycle),
    queryFn: () =>
      getCoComments(reportId, cycle === "current" ? { cycle: "current" } : {}),
    staleTime: 0,
    gcTime: 0,
  });
}
